// @spec SCF-003
// @spec SHL-STR-049
'use client';

import * as React from 'react';

import { THEME_CHANGE_EVENT } from '@/components/command-palette/command-registry';

type Theme = 'light' | 'dark' | 'system';

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

interface ThemeProviderState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeProviderContext = React.createContext<ThemeProviderState | undefined>(undefined);

/**
 * ThemeProvider component for managing application theme.
 *
 * Features:
 * - Persists theme preference to localStorage
 * - Supports 'light', 'dark', and 'system' themes
 * - Applies appropriate class to document root
 * - Respects system color scheme preference for 'system' mode
 *
 * @example
 * <ThemeProvider defaultTheme="system" storageKey="quantifier-theme">
 *   <App />
 * </ThemeProvider>
 */
export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'quantifier-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = React.useState<Theme>(() => {
    if (typeof window === 'undefined') {
      return defaultTheme;
    }
    const stored = localStorage.getItem(storageKey) as Theme | null;
    return stored ?? defaultTheme;
  });

  React.useEffect(() => {
    const root = window.document.documentElement;

    // Remove previous theme classes
    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }
  }, [theme]);

  // Listen for system theme changes when in 'system' mode
  React.useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      const root = window.document.documentElement;
      root.classList.remove('light', 'dark');
      root.classList.add(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [theme]);

  // Listen for theme change events from command palette
  React.useEffect(() => {
    const handleSetTheme = (e: CustomEvent<Theme>) => {
      localStorage.setItem(storageKey, e.detail);
      setTheme(e.detail);
    };

    window.addEventListener(THEME_CHANGE_EVENT, handleSetTheme as EventListener);
    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, handleSetTheme as EventListener);
    };
  }, [storageKey]);

  const value = React.useMemo(
    () => ({
      theme,
      setTheme: (newTheme: Theme) => {
        localStorage.setItem(storageKey, newTheme);
        setTheme(newTheme);
      },
    }),
    [theme, storageKey]
  );

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

/**
 * Hook to access the current theme and setTheme function.
 *
 * @example
 * const { theme, setTheme } = useTheme();
 * setTheme('dark');
 */
export function useTheme() {
  const context = React.useContext(ThemeProviderContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}
